import React, {useCallback, useEffect, useState} from "react";
import {Alert, Dialog, DialogContent, Snackbar, StyledEngineProvider, ThemeProvider, useTheme,} from "@mui/material";
import {User} from "./User";
import {createUser, getKeycloakUsers} from "./UsersApi";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {useTranslation} from "react-i18next";
import {keycloakUserTableCols} from "./KeycloakUsersImportTable";
import {ProcessDialogHeader} from "../../shared/components/dialogs/ProcessDialogHeader";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {muiDataTableTheme} from "../../shared/components/tables/muiDataTableTheme";
import {KeycloakUser} from "./KeycloakUser";
import {CenteredCircularProgress} from "../../shared/components/progress/CenteredCircularProgress";
import {CompanySelectField} from "../companies/CompanySelectField";
import {Company} from "../companies/Company";
import {useApplicationFormatter, useAssignableApplications} from "../applications/ApplicationProvider";
import MaterialReactTable from 'material-react-table';
import {RowSelectionState} from "@tanstack/table-core";
import {getMaterialTableTranslation} from "../../shared/technical/i18n/i18n";

export function KeycloakUsersImportDialog(props: {
  open: boolean,
  handleClose: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void,
}) {
  const theme = useTheme();
  const axios = useAxios();
  const {t} = useTranslation();
  const applicationFormatter = useApplicationFormatter();
  const filteredApplications = useAssignableApplications();
  const [keycloakUsers, setKeycloakUsers] = useState<KeycloakUser[] | undefined>();
  const [importErrors, setImportErrors] = useState<string>();
  const [currentlyImporting, setCurrentlyImporting] = useState(false);
  const [company, setCompany] = useState<Company>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const {open, handleClose} = props;

  useEffect(() => {
    if (!open) {
      return
    }
    getKeycloakUsers(axios).then(setKeycloakUsers)
  }, [axios, open]);

  const importUsers = useCallback(async () => {
    setCurrentlyImporting(true);
    const selectedIndexes = Object.keys(rowSelection).map(value => Number.parseInt(value));
    const selectedUsers = selectedIndexes.map((index: number) => keycloakUsers!![index]);
    console.info("Will import " + selectedUsers.map(user => user.email).join(","))
    const usersToCreate = selectedUsers.map(kcUser => kcUser as User).map(kcUser => ({...kcUser, company: company}));
    for (let i = 0; i < usersToCreate.length; i++) {
      try {
        await createUser(axios, usersToCreate[i]);
      } catch (error: any) {
        console.warn('Could not import user ' + usersToCreate[i].email, error);
        setImportErrors(old => (old || '') + error?.toString());
      }
    }
    handleClose();
    setCurrentlyImporting(false);
  }, [axios, handleClose, keycloakUsers, rowSelection, company]);

  useEffect(() => {
    importErrors && console.info('Import errors: ', importErrors);
  }, [importErrors]);

  const importButtonDisabled = !company || (Object.keys(rowSelection).length === 0) || currentlyImporting;
  return (
    <Dialog fullWidth={true} maxWidth={"md"} open={props.open} onClose={handleClose}
            aria-labelledby="keycloak users dialog" PaperProps={{sx: {minHeight: '400px', maxHeight: '90%', overflow: 'hidden',}}}>
      <DialogContent sx={{minHeight: '500px', backgroundColor: theme.palette.background.paper,}}>
        <ProcessDialogHeader title={t('KeycloakUsersImport.title')} handleClose={handleClose} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={muiDataTableTheme}>
            {!keycloakUsers &&
              <CenteredCircularProgress />
            }
            {keycloakUsers &&
              <MaterialReactTable columns={keycloakUserTableCols(t, applicationFormatter, filteredApplications)}
                                  data={keycloakUsers}
                                  initialState={{columnVisibility: {"keycloakId": false}, density: "compact"}}
                                  enablePagination={true}
                                  muiTablePaginationProps={{
                                    rowsPerPageOptions: [5, 10],
                                    showFirstButton: true,
                                    showLastButton: true,
                                  }}
                                  enableRowSelection={true}
                                  enableMultiRowSelection={true}
                                  onRowSelectionChange={setRowSelection}
                                  state={{rowSelection}}
                                  localization={getMaterialTableTranslation()}
              />
            }
          </ThemeProvider>
        </StyledEngineProvider>
        {importErrors &&
          <div>{importErrors}</div>
        }
      </DialogContent>
      <DialogActions>
        <div style={{marginLeft: 'auto', marginRight: '24px', minWidth: '240px'}}>
          <CompanySelectField name={"Company"} label={"Company"} value={company} onChange={setCompany} required={true} />
        </div>
        <Button onClick={importUsers} color="secondary" variant={"outlined"} disabled={importButtonDisabled}>
          {t('KeycloakUsersImport.btnImport')}
        </Button>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={!!importErrors}
          autoHideDuration={10000}
          onClose={() => setImportErrors(undefined)}
        >
          <Alert severity={'error'}>{t('KeycloakUsersImport.msgImportFailed') + importErrors}</Alert>
        </Snackbar>
      </DialogActions>
    </Dialog>
  );
}
